import { supabase } from "@/lib/supabase";
import { ACCEPTED_DOC_TYPES, MAX_DOC_SIZE, MAX_PHOTO_SIZE } from "@/lib/affiliateOptions";

function randomId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function safeExt(name: string, fallback = "jpg"): string {
  return (name.split(".").pop() || fallback).toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Optimiza una fotografía en el navegador: la reduce a un máximo de 1600 px
 * de ancho y la convierte a WebP para que el sitio cargue más rápido.
 * Si algo falla, devuelve el archivo original sin romper el formulario.
 */
export async function optimizePhoto(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const maxWidth = 1600;
    const scale = Math.min(1, maxWidth / bitmap.width);
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", 0.82),
    );
    if (!blob) return file;
    const base = file.name.replace(/\.[^.]+$/, "") || "foto";
    return new File([blob], `${base}.webp`, { type: "image/webp" });
  } catch {
    return file;
  }
}

/** Sube la fotografía (optimizada) de un vehículo al bucket público y devuelve su URL. */
export async function uploadAffiliatePhoto(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Selecciona una imagen válida para el vehículo.");
  }
  if (file.size > MAX_PHOTO_SIZE) {
    throw new Error("La fotografía no debe superar 6 MB.");
  }
  const optimized = await optimizePhoto(file);
  const path = `vehiculos/${randomId()}.${safeExt(optimized.name, "webp")}`;
  const { error } = await supabase.storage
    .from("affiliate-media")
    .upload(path, optimized, { cacheControl: "3600", upsert: false, contentType: optimized.type });
  if (error) throw new Error("No pudimos subir la fotografía. Intenta nuevamente.");
  const { data } = supabase.storage.from("affiliate-media").getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Sube un documento a un bucket privado y devuelve su ruta interna.
 * La ruta (no la URL) se guarda para generar enlaces temporales solo para el administrador.
 */
export async function uploadAffiliateDocument(
  file: File,
  folder: string,
): Promise<{ path: string; name: string }> {
  if (!ACCEPTED_DOC_TYPES.includes(file.type)) {
    throw new Error("Formato no permitido. Usa PDF, JPG, PNG o WebP.");
  }
  if (file.size > MAX_DOC_SIZE) {
    throw new Error("El documento no debe superar 8 MB.");
  }
  const path = `${folder}/${randomId()}.${safeExt(file.name, "pdf")}`;
  const { error } = await supabase.storage
    .from("affiliate-docs")
    .upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
  if (error) throw new Error("No pudimos subir el documento. Intenta nuevamente.");
  return { path, name: file.name };
}

/** Genera un enlace temporal (1 hora) para ver un documento privado. */
export async function getDocumentSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from("affiliate-docs").createSignedUrl(path, 3600);
  if (error || !data?.signedUrl) throw new Error("No pudimos abrir el documento.");
  return data.signedUrl;
}